import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { FiLock } from "react-icons/fi";
import { IoEye } from "react-icons/io5";
import { IoMdEyeOff } from "react-icons/io";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import { useModal } from "../../contexts/ModalContext";
import { useToast } from "../../contexts/ToastContext";
import useApi from "../../hooks/useApi";

const MODAL_ID = "gestaoUsuariosResetarSenha";
const RESET_URL = "/Painel/API/AccessManagement/GestaoUsuarios/ResetSenha/";
const MIN_LENGTH = 6;

const Title = styled.h3`
    margin: 0 0 6px 0;
    font-size: 18px;
    color: #263b4b;
`;

const Subtitle = styled.p`
    margin: 0 0 16px 0;
    font-size: 13px;
    color: #555;
`;

const Field = styled.label`
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 14px;
`;

const FieldLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: #374151;
    letter-spacing: 0.02em;
`;

const InputWrapper = styled.div`
    display: flex;
    align-items: center;
    height: 42px;
    background-color: #fff;
    border: 1px solid #94a3b8;
    border-radius: 8px;
    overflow: hidden;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus-within {
        border-color: #2563eb;
        box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
    }
`;

const IconBox = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 100%;
    color: #94a3b8;

    svg {
        width: 17px;
        height: 17px;
    }
`;

const Input = styled.input`
    flex: 1;
    height: 100%;
    border: none;
    outline: none;
    background: transparent;
    box-sizing: border-box;
    padding-right: 12px;
    font-size: 14px;
    color: #1f2937;
`;

const ShowPass = styled.button`
    all: unset;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 100%;
    cursor: pointer;
    color: #94a3b8;
    transition: color 0.15s ease;

    &:hover {
        color: #2563eb;
    }

    svg {
        width: 17px;
        height: 17px;
    }
`;

const ErrorMsg = styled.p`
    margin: 4px 0 0 0;
    font-size: 13px;
    color: #b91c1c;
`;

const BotoesWrapper = styled.div`
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
`;

const Botao = styled.button`
    padding: 9px 14px;
    background-color: ${(p) => (p.$cancel ? "#e5e7eb" : "#2563eb")};
    color: ${(p) => (p.$cancel ? "#333" : "#fff")};
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-weight: 600;
    font-size: 13px;
    transition: 0.2s all ease;

    &:disabled {
        background-color: ${(p) => (p.$cancel ? "#e5e7eb" : "#93c5fd")};
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        background-color: ${(p) => (p.$cancel ? "#d1d5db" : "#1d4ed8")};
    }
`;

const ModalResetarSenha = ({ usuario, onSaved }) => {
    const api = useApi();
    const { closeModal } = useModal();
    const { notifyError, notifySuccess } = useToast();

    const [novaSenha, setNovaSenha] = useState("");
    const [confirmarSenha, setConfirmarSenha] = useState("");
    const [showNova, setShowNova] = useState(false);
    const [showConfirmar, setShowConfirmar] = useState(false);
    const [saving, setSaving] = useState(false);
    const [erro, setErro] = useState("");

    const handleClose = () => {
        if (saving) return;
        closeModal(MODAL_ID);
    };

    const canSave = useMemo(
        () =>
            !saving &&
            novaSenha.length >= MIN_LENGTH &&
            novaSenha === confirmarSenha,
        [saving, novaSenha, confirmarSenha]
    );

    const handleConfirm = useCallback(async () => {
        if (saving || !usuario?.id) return;

        setErro("");

        if (novaSenha.length < MIN_LENGTH) {
            setErro(`A nova senha deve ter no mínimo ${MIN_LENGTH} caracteres.`);
            return;
        }

        if (novaSenha !== confirmarSenha) {
            setErro("As senhas não coincidem.");
            return;
        }

        setSaving(true);

        try {
            const { data } = await api.post(RESET_URL, {
                id: usuario.id,
                nova_senha: novaSenha,
                confirmar_senha: confirmarSenha,
            });

            notifySuccess(data?.message || "Senha resetada com sucesso.");
            await onSaved?.();
            closeModal(MODAL_ID);
        } catch (error) {
            console.error(error);
            const msg = error?.response?.data?.message || "Erro ao resetar a senha do usuário.";
            setErro(msg);
            notifyError(msg);
        } finally {
            setSaving(false);
        }
    }, [api, confirmarSenha, closeModal, novaSenha, notifyError, notifySuccess, onSaved, saving, usuario]);

    return (
        <Overlay state changeState={handleClose}>
            <Modal header close={handleClose} size="md">
                <Title>Resetar senha</Title>
                <Subtitle>
                    Defina uma nova senha para <strong>{usuario?.usuario || "o usuário"}</strong>.
                    No próximo acesso, o usuário será obrigado a trocá-la.
                </Subtitle>

                <Field>
                    <FieldLabel>NOVA SENHA</FieldLabel>
                    <InputWrapper>
                        <IconBox>
                            <FiLock />
                        </IconBox>
                        <Input
                            type={showNova ? "text" : "password"}
                            placeholder="Nova senha"
                            value={novaSenha}
                            onChange={(e) => setNovaSenha(e.target.value)}
                            autoComplete="new-password"
                        />
                        <ShowPass
                            type="button"
                            onClick={() => setShowNova((prev) => !prev)}
                            aria-label={showNova ? "Ocultar senha" : "Mostrar senha"}
                        >
                            {showNova ? <IoEye /> : <IoMdEyeOff />}
                        </ShowPass>
                    </InputWrapper>
                </Field>

                <Field>
                    <FieldLabel>CONFIRMAR NOVA SENHA</FieldLabel>
                    <InputWrapper>
                        <IconBox>
                            <FiLock />
                        </IconBox>
                        <Input
                            type={showConfirmar ? "text" : "password"}
                            placeholder="Repita a nova senha"
                            value={confirmarSenha}
                            onChange={(e) => setConfirmarSenha(e.target.value)}
                            autoComplete="new-password"
                        />
                        <ShowPass
                            type="button"
                            onClick={() => setShowConfirmar((prev) => !prev)}
                            aria-label={showConfirmar ? "Ocultar senha" : "Mostrar senha"}
                        >
                            {showConfirmar ? <IoEye /> : <IoMdEyeOff />}
                        </ShowPass>
                    </InputWrapper>
                </Field>

                {erro && <ErrorMsg>{erro}</ErrorMsg>}

                <BotoesWrapper>
                    <Botao $cancel type="button" onClick={handleClose} disabled={saving}>
                        CANCELAR
                    </Botao>
                    <Botao type="button" onClick={handleConfirm} disabled={!canSave}>
                        {saving ? "SALVANDO…" : "RESETAR SENHA"}
                    </Botao>
                </BotoesWrapper>
            </Modal>
        </Overlay>
    );
};

export default ModalResetarSenha;
export { MODAL_ID };
